import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import './DataPage.css';
import Pagination from '../components/Pagination';

import { API_URL } from '../config';
import { formatDate } from '../utils/formatDate';

function Orcamentos() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const pacienteIdFromUrl = searchParams.get('pacienteId');

  // Calcular data padrão (últimos 30 dias)
  const hoje = new Date();
  const trintaDiasAtras = new Date();
  trintaDiasAtras.setDate(hoje.getDate() - 30);

  const [orcamentos, setOrcamentos] = useState([]);
  const [allOrcamentos, setAllOrcamentos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);
  const [totalItems, setTotalItems] = useState(0);
  const [searchInput, setSearchInput] = useState('');
  const [dataInicio, setDataInicio] = useState(pacienteIdFromUrl ? '' : trintaDiasAtras.toISOString().split('T')[0]);
  const [dataFim, setDataFim] = useState(pacienteIdFromUrl ? '' : hoje.toISOString().split('T')[0]);
  const [convenio, setConvenio] = useState('');
  const [convertido, setConvertido] = useState('');
  const [motivoPerda, setMotivoPerda] = useState('');
  const [pacienteId, setPacienteId] = useState(pacienteIdFromUrl || '');
  const [sortBy, setSortBy] = useState('DATA');
  const [sortOrder, setSortOrder] = useState('DESC');
  const [stats, setStats] = useState({ total: 0, convertidos: 0, naoConvertidos: 0, hoje: 0 });
  const [filters, setFilters] = useState({ convenios: [], motivos: [] });

  // Fetch on mount + when page/sort changes
  useEffect(() => {
    fetchOrcamentos();
  }, [currentPage, sortBy, sortOrder]);

  const handleSearch = () => {
    setCurrentPage(1);
    fetchOrcamentos(1);
  };

  const fetchOrcamentos = async (page) => {
    const activePage = page || currentPage;
    setLoading(true);
    try {
      const params = new URLSearchParams({
        sortBy,
        sortOrder
      });

      if (searchInput) params.append('search', searchInput);
      if (dataInicio) params.append('dataInicio', dataInicio);
      if (dataFim) params.append('dataFim', dataFim);
      if (convenio) params.append('convenio', convenio);
      if (convertido !== '') params.append('convertido', convertido);
      if (motivoPerda) params.append('motivo_perda', motivoPerda);
      if (pacienteId) params.append('pacienteId', pacienteId);

      const response = await axios.get(`${API_URL}/orcamentos?${params.toString()}`);
      const data = response.data.data || [];
      setAllOrcamentos(data);
      setTotalItems(data.length);

      // Aplicar paginação
      const startIndex = (activePage - 1) * itemsPerPage;
      const endIndex = startIndex + itemsPerPage;
      const paginatedData = data.slice(startIndex, endIndex);
      setOrcamentos(paginatedData);

      setStats(response.data.stats || { total: 0, valorTotal: 0, valorMedio: 0, convertidos: 0, naoConvertidos: 0, hoje: 0 });
      setFilters(response.data.filters || { convenios: [], motivos: [] });
    } catch (err) {
      console.error('Erro ao buscar orçamentos:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'ASC' ? 'DESC' : 'ASC');
    } else {
      setSortBy(field);
      setSortOrder('ASC');
    }
  };

  const getSortIcon = (field) => {
    if (sortBy !== field) return '↕️';
    return sortOrder === 'ASC' ? '↑' : '↓';
  };

  // formatDate importado de ../utils/formatDate

  const formatCurrency = (value) => {
    if (!value) return 'R$ 0,00';
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
  };

  const clearFilters = () => {
    setSearchInput('');
    setConvenio('');
    setConvertido('');
    setMotivoPerda('');
    setPacienteId('');
    navigate('/orcamentos');
    const hoje = new Date();
    const trintaDiasAtras = new Date();
    trintaDiasAtras.setDate(hoje.getDate() - 30);
    setDataInicio(trintaDiasAtras.toISOString().split('T')[0]);
    setDataFim(hoje.toISOString().split('T')[0]);
    setCurrentPage(1);
  };

  if (loading && orcamentos.length === 0) {
    return <div className="loading">Carregando orçamentos...</div>;
  }

  const styles = {
    pageHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      marginBottom: '24px',
      flexWrap: 'wrap',
      gap: '12px',
    },
    pageIcon: {
      fontSize: '1.5rem',
      lineHeight: 1,
    },
    pageTitle: {
      fontSize: '1.5rem',
      fontWeight: '700',
      color: '#1a1a2e',
      margin: '0 0 2px 0',
    },
    pageSubtitle: {
      fontSize: '0.85rem',
      color: '#666',
      margin: 0,
    },
    btnPesquisar: {
      padding: '8px 20px',
      backgroundColor: '#2563eb',
      color: '#fff',
      border: 'none',
      borderRadius: '6px',
      cursor: 'pointer',
      fontSize: '0.85rem',
      fontWeight: '600',
      whiteSpace: 'nowrap',
    },
  };

  return (
    <div className="data-page">
      <div style={styles.pageHeader}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span style={styles.pageIcon}>💰</span>
          <div>
            <h1 style={styles.pageTitle}>Orçamentos</h1>
            <p style={styles.pageSubtitle}>
              Exibindo {orcamentos.length} de {totalItems} orçamentos
              {(searchInput || dataInicio || dataFim || convenio || convertido !== '' || motivoPerda || pacienteId) && ' (filtrado)'}
            </p>
          </div>
        </div>
      </div>

      {pacienteId && (
        <div style={{
          background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
          color: 'white',
          padding: '1rem 1.5rem',
          borderRadius: '12px',
          marginBottom: '1.5rem',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          boxShadow: '0 4px 12px rgba(245, 158, 11, 0.3)'
        }}>
          <div>
            <strong>👤 Filtrando por Paciente ID: {pacienteId}</strong>
          </div>
          <button
            onClick={clearFilters}
            style={{
              background: 'rgba(255, 255, 255, 0.2)',
              border: 'none',
              color: 'white',
              padding: '0.5rem 1rem',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: '600',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.3)'}
            onMouseLeave={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.2)'}
          >
            ✕ Remover Filtro
          </button>
        </div>
      )}

      {/* Estatísticas */}
      <div className="compact-stats">
        <div className="compact-stat-card purple">
          <div className="compact-stat-value">{(stats.total || 0).toLocaleString('pt-BR')}</div>
          <div className="compact-stat-label">Total Orçamentos</div>
        </div>
        <div className="compact-stat-card green">
          <div className="compact-stat-value">{(stats.convertidos || 0).toLocaleString('pt-BR')}</div>
          <div className="compact-stat-label">Convertidos</div>
        </div>
        <div className="compact-stat-card red">
          <div className="compact-stat-value">{(stats.naoConvertidos || 0).toLocaleString('pt-BR')}</div>
          <div className="compact-stat-label">Não Convertidos</div>
        </div>
        <div className="compact-stat-card blue">
          <div className="compact-stat-value">R$ {(stats.valorTotal || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</div>
          <div className="compact-stat-label">Valor Total</div>
        </div>
        <div className="compact-stat-card orange">
          <div className="compact-stat-value">{(stats.hoje || 0).toLocaleString('pt-BR')}</div>
          <div className="compact-stat-label">Hoje</div>
        </div>
      </div>

      <div className="filters-section">
        <div className="search-bar" style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <input
            type="text"
            placeholder="Buscar por ID, nome, CPF, email ou telefone... (pressione Enter)"
            value={searchInput}
            onChange={(e) => {
              setSearchInput(e.target.value);
              if (e.target.value) {
                setDataInicio('');
                setDataFim('');
              }
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleSearch();
              }
            }}
            className="search-input"
          />
          <button onClick={handleSearch} style={styles.btnPesquisar}>
            Pesquisar
          </button>
        </div>

        <div className="filters-row">
          <div className="filter-group">
            <label>Data Início:</label>
            <input
              type="date"
              value={dataInicio}
              onChange={(e) => setDataInicio(e.target.value)}
              className="filter-select"
            />
          </div>

          <div className="filter-group">
            <label>Data Fim:</label>
            <input
              type="date"
              value={dataFim}
              onChange={(e) => setDataFim(e.target.value)}
              className="filter-select"
            />
          </div>

          <div className="filter-group">
            <label>Convênio:</label>
            <select
              value={convenio}
              onChange={(e) => setConvenio(e.target.value)}
              className="filter-select"
            >
              <option value="">Todos</option>
              {filters.convenios.map((c, idx) => (
                <option key={idx} value={c}>{c}</option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>Status:</label>
            <select
              value={convertido}
              onChange={(e) => setConvertido(e.target.value)}
              className="filter-select"
            >
              <option value="">Todos</option>
              <option value="true">Convertido</option>
              <option value="false">Não Convertido</option>
            </select>
          </div>

          <div className="filter-group">
            <label>Motivo de Perda:</label>
            <select
              value={motivoPerda}
              onChange={(e) => setMotivoPerda(e.target.value)}
              className="filter-select"
            >
              <option value="">Todos</option>
              {filters.motivos.map((m) => (
                <option key={m.ID} value={m.ID}>{m.DESCRICAO}</option>
              ))}
            </select>
          </div>

          <button onClick={clearFilters} className="clear-filters-btn">
            🔄 Limpar Filtros
          </button>
        </div>
      </div>

      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th onClick={() => handleSort('ID')} style={{ cursor: 'pointer' }}>
                ID {getSortIcon('ID')}
              </th>
              <th onClick={() => handleSort('DATA')} style={{ cursor: 'pointer' }}>
                Data {getSortIcon('DATA')}
              </th>
              <th onClick={() => handleSort('NOME_PACIENTE')} style={{ cursor: 'pointer' }}>
                Paciente {getSortIcon('NOME_PACIENTE')}
              </th>
              <th>CPF</th>
              <th onClick={() => handleSort('CONVENIO')} style={{ cursor: 'pointer' }}>
                Convênio {getSortIcon('CONVENIO')}
              </th>
              <th>Plano</th>
              <th>Itens</th>
              <th onClick={() => handleSort('TOTAL')} style={{ cursor: 'pointer' }}>
                Total {getSortIcon('TOTAL')}
              </th>
              <th>Status</th>
              <th>Motivo Perda</th>
              <th>Ações</th>
            </tr>
          </thead>
          <tbody>
            {orcamentos.map((orc) => (
              <tr key={orc.ID}>
                <td><strong>{orc.ID}</strong></td>
                <td>{formatDate(orc.DATA)}</td>
                <td>{orc.NOME_PACIENTE || orc.PACIENTE_NOME_COMPLETO || '-'}</td>
                <td>{orc.PACIENTE_CPF || '-'}</td>
                <td>{orc.CONVENIO || '-'}</td>
                <td>{orc.PLANO || '-'}</td>
                <td>{orc.TOTAL_ITENS || 0}</td>
                <td><strong>{formatCurrency(orc.TOTAL)}</strong></td>
                <td>
                  <span style={{
                    padding: '0.25rem 0.5rem',
                    borderRadius: '4px',
                    fontSize: '0.85rem',
                    fontWeight: '600',
                    background: orc.CONVERTIDO ? '#d1fae5' : '#fee2e2',
                    color: orc.CONVERTIDO ? '#059669' : '#dc2626'
                  }}>
                    {orc.CONVERTIDO ? '✓ Convertido' : '✗ Não Convertido'}
                  </span>
                </td>
                <td>{orc.MOTIVO_PERDA_DESC || '-'}</td>
                <td>
                  <button
                    onClick={() => navigate(`/orcamentos/${orc.ID}`)}
                    className="view-button"
                  >
                    👁️ Ver
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Paginação */}
      <Pagination
        currentPage={currentPage}
        totalPages={Math.ceil(totalItems / itemsPerPage)}
        onPageChange={setCurrentPage}
        itemsPerPage={itemsPerPage}
        totalItems={totalItems}
      />

      {orcamentos.length === 0 && !loading && (
        <div className="no-data">
          <p>Nenhum orçamento encontrado com os filtros aplicados.</p>
          <button onClick={clearFilters} className="clear-filters-btn">
            🔄 Limpar Filtros
          </button>
        </div>
      )}
    </div>
  );
}

export default Orcamentos;
